import { useEffect, useState } from 'react';
import { X, Timer, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { trackGTMEvent } from '@/components/GoogleTagManager';
import { useBannerContext } from '@/contexts/BannerContext';
import { useTranslation } from 'react-i18next';

interface ComebackData {
  prospect_id: string;
  discount_percentage: number;
  expires_at: string;
  expired: boolean;
  timestamp: number;
}

interface FunnelSession {
  email: string;
  prospect_id?: string;
  interests?: string;
  plan?: 'monthly' | 'yearly';
  timestamp: number;
}

export const ComebackBanner = () => {
  const navigate = useNavigate();
  const { setIsBannerVisible } = useBannerContext();
  const { t } = useTranslation();
  const [showBanner, setShowBanner] = useState(false);
  const [bannerType, setBannerType] = useState<'discount' | 'resume' | null>(null);
  const [comebackData, setComebackData] = useState<ComebackData | null>(null);
  const [funnelSession, setFunnelSession] = useState<FunnelSession | null>(null);
  const [timeLeft, setTimeLeft] = useState<string>('');

  useEffect(() => {
    // VERIFICAÇÃO PRIMÁRIA: Não mostrar banner se já tem subscription ativa
    const activeSession = localStorage.getItem('newsletter_session');
    if (activeSession) {
      try {
        const parsed = JSON.parse(activeSession);
        // Verificar se tem session_token E email (indica subscription ativa)
        if (parsed.session_token && parsed.email) {
          console.log('[ComebackBanner] User already has active subscription, hiding banner');
          setShowBanner(false);
          setIsBannerVisible(false);
          
          // Limpar dados antigos do funil (caso não tenham sido limpos antes)
          localStorage.removeItem('comeback_prospect');
          localStorage.removeItem('funnel_session');
          return; // Sair do useEffect sem mostrar banner
        }
      } catch (err) {
        // Se parsing falhar, continuar fluxo normal
        console.error('[ComebackBanner] Error parsing newsletter_session:', err);
      }
    }

    // PRIORIDADE 1: Comeback com desconto
    const comebackDataStr = localStorage.getItem('comeback_prospect');
    if (comebackDataStr) {
      try {
        const parsed: ComebackData = JSON.parse(comebackDataStr);
        
        // Check if has valid discount
        if (parsed.discount_percentage && parsed.discount_percentage > 0) {
          const expiresAt = new Date(parsed.expires_at);
          const now = new Date();

          // Check if not expired
          if (now <= expiresAt) {
            setComebackData(parsed);
            setBannerType('discount');
            setShowBanner(true);
            setIsBannerVisible(true);

            // Update countdown timer every second
            const interval = setInterval(() => {
              const now = new Date();
              const diff = expiresAt.getTime() - now.getTime();

              if (diff <= 0) {
                setTimeLeft(t('comebackBanner.discount.expired'));
                localStorage.removeItem('comeback_prospect');
                setShowBanner(false);
                setIsBannerVisible(false);
                clearInterval(interval);
                return;
              }

              const hours = Math.floor(diff / (1000 * 60 * 60));
              const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
              const seconds = Math.floor((diff % (1000 * 60)) / 1000);
              
              if (hours > 0) {
                setTimeLeft(`${hours}h ${minutes}m`);
              } else {
                setTimeLeft(`${minutes}m ${seconds}s`);
              }
            }, 1000);

            return () => clearInterval(interval);
          } else {
            // Remove expired comeback
            localStorage.removeItem('comeback_prospect');
          }
        }
      } catch (err) {
        console.error('[ComebackBanner] Error parsing comeback:', err);
        localStorage.removeItem('comeback_prospect');
      }
    }

    // PRIORIDADE 2: Retomada orgânica (funnel_session)
    const sessionDataStr = localStorage.getItem('funnel_session');
    if (sessionDataStr) {
      try {
        const parsed: FunnelSession = JSON.parse(sessionDataStr);
        const tokenAge = Date.now() - parsed.timestamp;
        
        // Validate session not expired (1h) and has minimum data
        if (tokenAge < 3600000 && parsed.email) {
          setFunnelSession(parsed);
          setBannerType('resume');
          setShowBanner(true);
          setIsBannerVisible(true);
          return;
        } else {
          // Clean expired session
          localStorage.removeItem('funnel_session');
        }
      } catch (err) {
        console.error('[ComebackBanner] Error parsing session:', err);
        localStorage.removeItem('funnel_session');
      }
    }

    // No valid scenario
    setShowBanner(false);
    setIsBannerVisible(false);
  }, [setIsBannerVisible]);

  const handleContinue = () => {
    // Get prospect_id from either source
    const prospectId = comebackData?.prospect_id || funnelSession?.prospect_id;
    
    if (!prospectId) {
      console.error('[ComebackBanner] No prospect_id available');
      return;
    }

    // Track event based on banner type
    if (bannerType === 'discount') {
      trackGTMEvent('comeback_banner_clicked', { 
        discount: comebackData?.discount_percentage,
        prospect_id: prospectId
      });
    } else {
      trackGTMEvent('funnel_resume_banner_clicked', { 
        prospect_id: prospectId
      });
    }
    
    // Always redirect to /comeback - it will handle validation and intelligent redirection
    navigate(`/comeback?prospect_id=${prospectId}`);
  };

  const handleDismiss = () => {
    if (bannerType === 'discount') {
      trackGTMEvent('comeback_banner_dismissed', { 
        discount: comebackData?.discount_percentage 
      });
    } else {
      trackGTMEvent('funnel_resume_banner_dismissed');
    }
    setShowBanner(false);
    setIsBannerVisible(false);
  };

  if (!showBanner || (!comebackData && !funnelSession)) return null;

  return (
    <div className={`fixed top-0 left-0 right-0 z-50 shadow-xl animate-slide-down text-white 
      ${bannerType === 'discount' 
        ? 'bg-gradient-to-r from-newsletter-primary via-purple-600 to-pink-600' 
        : 'bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600'
    }`}>
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          {/* Left: Message */}
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <Sparkles className="w-5 h-5 flex-shrink-0 animate-pulse" />
            <div className="flex-1 min-w-0">
              {bannerType === 'discount' ? (
                <>
                  <p className="font-bold text-sm sm:text-base truncate">
                    {t('comebackBanner.discount.title', { percentage: comebackData?.discount_percentage })}
                  </p>
                  <p className="text-xs opacity-90 flex items-center gap-1">
                    <Timer className="w-3 h-3" />
                    {t('comebackBanner.discount.expiresIn')} <span className="font-mono font-bold">{timeLeft}</span>
                  </p>
                </>
              ) : (
                <>
              <p className="font-bold text-sm sm:text-base">
                    {t('comebackBanner.resume.title')}
                  </p>
                  <p className="text-xs opacity-90">
                    {t('comebackBanner.resume.subtitle')}
                  </p>
                </>
              )}
            </div>
          </div>
          
          {/* Right: Actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button 
              onClick={handleContinue}
              size="sm"
              className="bg-white text-newsletter-primary hover:bg-gray-100 font-bold shadow-lg"
            >
              {t('comebackBanner.actions.continue')}
            </Button>
            <button 
              onClick={handleDismiss}
              className="p-1.5 hover:bg-white/20 rounded-full transition-colors"
              aria-label={t('comebackBanner.actions.dismiss')}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
